export const GRID = {
  margin: 32,
  poolPad: 28,
  poolHeader: 40,
  poolGap: 152,
  colW: 210,
  rowH: 96,
  branchRowH: 100,
  nodeW: 162,
  nodeH: 56,
  eventR: 14,
  eventGap: 20,
  gwHalf: 18,
  msgLaneStep: 24,
}

function clamp(v, lo, hi) {
  return Math.min(hi, Math.max(lo, v))
}

function orthPath(points) {
  return points.map((p) => p.join(',')).join(' ')
}

function isEventNode(pos) {
  const t = pos.node?.type
  return t === 'start' || t === 'end'
}

function nodeSize(pos) {
  if (isEventNode(pos)) {
    const d = GRID.eventR * 2
    return { w: d, h: d }
  }
  return { w: pos.w || GRID.nodeW, h: GRID.nodeH }
}

function eventCx(pos) {
  return pos.x + GRID.eventR
}

function eventCy(pos) {
  return pos.y + GRID.nodeH / 2
}

function nodeCx(pos) {
  const { w } = nodeSize(pos)
  return pos.x + w / 2
}

function anchor(pos, side, alignX) {
  const { w, h } = nodeSize(pos)
  const cx = isEventNode(pos) ? eventCx(pos) : pos.x + w / 2
  const cy = isEventNode(pos) ? eventCy(pos) : pos.y + h / 2
  const x = alignX == null ? cx : clamp(alignX, pos.x + 10, pos.x + w - 10)

  if (isEventNode(pos)) {
    if (side === 'right') return { x: pos.x + w, y: cy }
    if (side === 'left') return { x: pos.x, y: cy }
    if (side === 'bottom') return { x: cx, y: pos.y + h }
    return { x: cx, y: pos.y }
  }

  if (pos.node?.type === 'gateway') {
    const g = GRID.gwHalf
    if (side === 'right') return { x: cx + g, y: cy }
    if (side === 'left') return { x: cx - g, y: cy }
    if (side === 'bottom') return { x, y: cy + g }
    return { x, y: cy - g }
  }

  if (side === 'right') return { x: pos.x + w, y: cy }
  if (side === 'left') return { x: pos.x, y: cy }
  if (side === 'bottom') return { x, y: pos.y + h }
  return { x, y: pos.y }
}

function parsePathPoints(pointsStr) {
  const nums = pointsStr.split(/[\s,]+/).map(Number).filter((n) => !Number.isNaN(n))
  const pts = []
  for (let i = 0; i + 1 < nums.length; i += 2) pts.push([nums[i], nums[i + 1]])
  return pts
}

const ARROW_STUB = 14
const ARROW_STUB_VERT = 18
const ARROW_HEAD = 8
const LABEL_BOX_PAD = 2
const LABEL_BOX_H = 18

/** Координаты древка без наконечника (marker refX=7, ширина 8). */
function segmentShaftExtents(seg) {
  if (seg.horizontal) {
    const left = Math.min(seg.x1, seg.x2)
    const right = Math.max(seg.x1, seg.x2)
    const toRight = seg.x2 >= seg.x1
    return toRight
      ? { start: left, end: right - ARROW_HEAD }
      : { start: left + ARROW_HEAD, end: right }
  }
  const top = Math.min(seg.y1, seg.y2)
  const bottom = Math.max(seg.y1, seg.y2)
  return seg.downward
    ? { start: top, end: bottom - ARROW_HEAD }
    : { start: top + ARROW_HEAD, end: bottom }
}

/** Подпись по центру самого длинного сегмента пути (поверх линии в SVG). */
function labelOnSegment(pointsStr, labelText = '') {
  const pts = parsePathPoints(pointsStr)
  let best = null
  let bestLen = 0

  for (let i = 0; i < pts.length - 1; i += 1) {
    const [x1, y1] = pts[i]
    let [x2, y2] = pts[i + 1]
    const horizontal = Math.abs(y2 - y1) < 0.5
    const vertical = Math.abs(x2 - x1) < 0.5
    if (!horizontal && !vertical) continue

    if (vertical) {
      let j = i + 1
      while (j < pts.length - 1) {
        const [nx1, ny1] = pts[j]
        const [nx2, ny2] = pts[j + 1]
        if (Math.abs(nx2 - nx1) >= 0.5 || Math.abs(nx1 - x1) >= 0.5 || Math.abs(ny1 - y2) >= 0.5) break
        y2 = ny2
        j += 1
      }
    }

    const len = Math.abs(x2 - x1) + Math.abs(y2 - y1)
    if (len < 12) continue
    if (len > bestLen) {
      bestLen = len
      best = { x1, y1, x2, y2, horizontal, vertical, downward: y2 > y1 }
    }
  }

  if (!best) return { labelX: 0, labelY: 0, labelAnchor: 'middle' }

  const display = displayLabelText(labelText)
  const shaft = segmentShaftExtents(best)

  if (best.horizontal) {
    let labelX = (shaft.start + shaft.end) / 2

    if (display) {
      const half = labelCoverHalf(display, 'horizontal')
      const minX = shaft.start + half
      const maxX = shaft.end - half
      if (minX <= maxX) {
        labelX = clamp(labelX, minX, maxX)
      }
    }

    return { labelX, labelY: best.y1, labelAnchor: 'middle' }
  }

  let labelY = (shaft.start + shaft.end) / 2

  if (display) {
    const half = labelCoverHalf(display, 'vertical')
    const minY = shaft.start + half
    const maxY = shaft.end - half
    if (minY <= maxY) {
      labelY = clamp(labelY, minY, maxY)
    }
  }

  return { labelX: best.x1, labelY, labelAnchor: 'middle' }
}

const ROW_THRESH = 40
const COL_STACKED = GRID.nodeW * 0.35
const LABEL_GAP_PAD = 20
const DETOUR_PAD = 22
/** Дуга дальней ветви веера — выше остальных горизонталей. */
const TOP_ARC_EXTRA = 30
const DETOUR_LIFT_STEP = 16

function nodesBetweenOnRow(nodePositions, src, tgt) {
  const srcCol = src.node?.col ?? 0
  const tgtCol = tgt.node?.col ?? 0
  const srcRow = src.node?.row ?? 0
  if (srcCol >= tgtCol) return []

  return Object.values(nodePositions).filter((p) => {
    if (p.poolId !== src.poolId) return false
    const col = p.node?.col ?? 0
    const row = p.node?.row ?? 0
    if (row !== srcRow) return false
    if (col <= srcCol || col >= tgtCol) return false
    const t = p.node?.type
    return t !== 'start' && t !== 'end' && t !== 'gateway'
  })
}

function nodeBounds(pos) {
  const { w, h } = nodeSize(pos)
  return { left: pos.x, right: pos.x + w, top: pos.y, bottom: pos.y + h }
}

function needsDetourRoute(src, tgt, nodePositions) {
  if (Math.abs(src.y - tgt.y) > ROW_THRESH) return false
  if (nodeCx(tgt) <= nodeCx(src)) return false
  return nodesBetweenOnRow(nodePositions, src, tgt).length > 0
}

function sameRowPairKey(sourceId, targetId, nodePositions) {
  const src = nodePositions[sourceId]
  const tgt = nodePositions[targetId]
  if (!src || !tgt) return ''
  const a = nodeCx(src)
  const b = nodeCx(tgt)
  return a <= b ? `${sourceId}|${targetId}` : `${targetId}|${sourceId}`
}

function isSameRowForward(edge, nodePositions) {
  const src = nodePositions[edge.source]
  const tgt = nodePositions[edge.target]
  return src && tgt
    && Math.abs(src.y - tgt.y) <= ROW_THRESH
    && nodeCx(tgt) > nodeCx(src)
}

function sameRowPeers(edge, sequenceRaw, nodePositions) {
  const src = nodePositions[edge.source]
  const tgt = nodePositions[edge.target]
  if (!src || !tgt) return []
  if (Math.abs(src.y - tgt.y) > ROW_THRESH) return []

  const pairKey = sameRowPairKey(edge.source, edge.target, nodePositions)
  if (!pairKey) return []

  return sequenceRaw.filter((e) => {
    if (sameRowPairKey(e.source, e.target, nodePositions) !== pairKey) return false
    const s = nodePositions[e.source]
    const t = nodePositions[e.target]
    return s && t && Math.abs(s.y - t.y) <= ROW_THRESH
  })
}

/** Несколько исходящих стрелок с одного этапа на одном ряду (approval→waiting и approval→done). */
function sameRowOutgoingPeers(edge, sequenceRaw, nodePositions) {
  if (!isSameRowForward(edge, nodePositions)) return []

  return sequenceRaw.filter((e) => e.source === edge.source && isSameRowForward(e, nodePositions))
}

function sameRowRoutePeers(edge, sequenceRaw, nodePositions) {
  const outgoing = sameRowOutgoingPeers(edge, sequenceRaw, nodePositions)
  if (outgoing.length >= 2) return outgoing

  const pair = sameRowPeers(edge, sequenceRaw, nodePositions)
  if (pair.length >= 2) return pair

  return []
}

function orderRoutePeers(peers, nodePositions) {
  const isFanOut = peers.length >= 2
    && peers.every((e) => isSameRowForward(e, nodePositions))
    && new Set(peers.map((e) => e.source)).size === 1

  if (isFanOut) {
    return [...peers].sort((a, b) => {
      const dx = nodeCx(nodePositions[a.target]) - nodeCx(nodePositions[b.target])
      if (dx !== 0) return dx
      return String(a.id).localeCompare(String(b.id))
    })
  }

  return [...peers].sort((a, b) => {
    const aFwd = nodeCx(nodePositions[a.target]) >= nodeCx(nodePositions[a.source]) ? 0 : 1
    const bFwd = nodeCx(nodePositions[b.target]) >= nodeCx(nodePositions[b.source]) ? 0 : 1
    if (aFwd !== bFwd) return aFwd - bFwd
    const aUi = a.dashed ? 1 : 0
    const bUi = b.dashed ? 1 : 0
    if (aUi !== bUi) return aUi - bUi
    return String(a.id).localeCompare(String(b.id))
  })
}

function isFanOutPeers(peers, nodePositions) {
  return peers.length >= 2
    && peers.every((e) => isSameRowForward(e, nodePositions))
    && new Set(peers.map((e) => e.source)).size === 1
}

function hasReversePeer(edge, sequenceRaw, nodePositions) {
  return sequenceRaw.some((e) => {
    if (e.source !== edge.target || e.target !== edge.source) return false
    const s = nodePositions[e.source]
    const t = nodePositions[e.target]
    return s && t && Math.abs(s.y - t.y) <= ROW_THRESH
  })
}

/**
 * Веер: дальняя цель — дугой сверху; ближняя — прямо или сверху (если есть обратная).
 * Пара туда‑обратно: вперёд сверху, назад — прямо по центру (не снизу, чтобы не слипаться).
 */
function resolveGroupedStaple(edge, peers, src, tgt, nodePositions, priorEdges, sequenceRaw) {
  const ordered = orderRoutePeers(peers, nodePositions)
  const idx = ordered.findIndex((e) => e.id === edge.id)
  if (idx < 0) return pickDetourStaple(src, tgt, nodePositions, priorEdges)

  const isForward = nodeCx(tgt) >= nodeCx(src)
  const pairedBack = hasReversePeer(edge, sequenceRaw, nodePositions)

  if (isFanOutPeers(peers, nodePositions)) {
    if (idx === ordered.length - 1) {
      return pickDetourStaple(src, tgt, nodePositions, priorEdges, TOP_ARC_EXTRA)
    }
    if (pairedBack) {
      return isForward
        ? pickParallelStaple(src, tgt, 0, nodePositions, priorEdges)
        : { straight: true }
    }
    if (!needsDetourRoute(src, tgt, nodePositions)) {
      return { straight: true }
    }
    return pickParallelStaple(src, tgt, 1 + idx * 2, nodePositions, priorEdges)
  }

  if (isForward) {
    return pickParallelStaple(src, tgt, 0, nodePositions, priorEdges)
  }
  return { straight: true }
}

function peerRouteLane(edge, sequenceRaw, nodePositions) {
  const peers = sameRowRoutePeers(edge, sequenceRaw, nodePositions)
  if (peers.length <= 1) return null

  const ordered = orderRoutePeers(peers, nodePositions)
  const idx = ordered.findIndex((e) => e.id === edge.id)
  return idx >= 0 ? idx : null
}

function shouldStapleRoute(src, tgt, nodePositions, edge, sequenceRaw) {
  if (Math.abs(src.y - tgt.y) > ROW_THRESH) return false
  if (sameRowRoutePeers(edge, sequenceRaw, nodePositions).length >= 2) return true
  return needsDetourRoute(src, tgt, nodePositions)
}

function edgeRoutePriority(e, nodePositions, sequenceRaw) {
  const src = nodePositions[e.source]
  const tgt = nodePositions[e.target]
  if (!src || !tgt) return 0
  const lane = peerRouteLane(e, sequenceRaw, nodePositions)
  const peers = sameRowRoutePeers(e, sequenceRaw, nodePositions)
  if (peers.length >= 2 && lane != null) {
    const isFarthestFanOut = isFanOutPeers(peers, nodePositions) && lane === peers.length - 1
    return isFarthestFanOut ? 50 : lane + 1
  }
  if (needsDetourRoute(src, tgt, nodePositions)) return 20
  return 0
}

function horizontalBusHitsNodes(busY, xLeft, xRight, nodePositions, poolId, excludeIds) {
  const xl = Math.min(xLeft, xRight)
  const xr = Math.max(xLeft, xRight)
  let hits = 0

  for (const pos of Object.values(nodePositions)) {
    const id = pos.node?.id
    if (!id || excludeIds.has(id) || pos.poolId !== poolId) continue
    const b = nodeBounds(pos)
    if (busY >= b.top - 2 && busY <= b.bottom + 2 && xr >= b.left && xl <= b.right) {
      hits += 1
    }
  }
  return hits
}

function countBusCrossings(busY, xLeft, xRight, priorEdges) {
  const xl = Math.min(xLeft, xRight)
  const xr = Math.max(xLeft, xRight)
  let hits = 0

  for (const edge of priorEdges) {
    if (!edge.points) continue
    const pts = parsePathPoints(edge.points)
    for (let i = 0; i < pts.length - 1; i += 1) {
      const [x1, y1] = pts[i]
      const [x2, y2] = pts[i + 1]
      const horiz = Math.abs(y2 - y1) < 0.5
      const vert = Math.abs(x2 - x1) < 0.5
      if (horiz && Math.abs(y1 - busY) < 2) {
        const sl = Math.min(x1, x2)
        const sr = Math.max(x1, x2)
        if (xr >= sl && xl <= sr) hits += 1
      }
      if (vert) {
        const yt = Math.min(y1, y2)
        const yb = Math.max(y1, y2)
        if (x1 >= xl && x1 <= xr && busY >= yt && busY <= yb) hits += 1
      }
    }
  }
  return hits
}

function pickDetourStaple(src, tgt, nodePositions, priorEdges = [], extraLift = 0) {
  const obstacles = nodesBetweenOnRow(nodePositions, src, tgt)
  const srcB = nodeBounds(src)
  const tgtB = nodeBounds(tgt)
  const boxes = [srcB, tgtB, ...obstacles.map(nodeBounds)]
  const xLeft = nodeCx(src)
  const xRight = nodeCx(tgt)
  const exclude = new Set([src.node?.id, tgt.node?.id].filter(Boolean))

  const baseUp = Math.min(...boxes.map((b) => b.top)) - DETOUR_PAD - extraLift

  const score = (busY) => (
    horizontalBusHitsNodes(busY, xLeft, xRight, nodePositions, src.poolId, exclude)
    + countBusCrossings(busY, xLeft, xRight, priorEdges)
  )

  let busY = baseUp
  let lift = 0
  while (score(busY) > 0 && lift < 120) {
    lift += DETOUR_LIFT_STEP
    busY = baseUp - lift
  }

  return { side: 'top', busY }
}

/** Скобка для параллельных рёбер между одной парой узлов (lane 1 — сверху, 2 — снизу, …). */
function pickParallelStaple(src, tgt, laneIndex, nodePositions, priorEdges = []) {
  const obstacles = nodesBetweenOnRow(nodePositions, src, tgt)
  const srcB = nodeBounds(src)
  const tgtB = nodeBounds(tgt)
  const boxes = [srcB, tgtB, ...obstacles.map(nodeBounds)]
  const xLeft = nodeCx(src)
  const xRight = nodeCx(tgt)
  const exclude = new Set([src.node?.id, tgt.node?.id].filter(Boolean))
  const stack = Math.floor(laneIndex / 2) * 12

  const busYUp = Math.min(...boxes.map((b) => b.top)) - DETOUR_PAD - stack
  const busYDown = Math.max(...boxes.map((b) => b.bottom)) + DETOUR_PAD + stack
  const useTop = laneIndex % 2 === 0

  if (useTop) {
    return { side: 'top', busY: busYUp }
  }
  return { side: 'bottom', busY: busYDown }
}

export function displayLabelText(text) {
  if (!text || !String(text).trim()) return ''
  const trimmed = text.trim()
  return trimmed.length > 30 ? `${trimmed.slice(0, 30)}…` : trimmed
}

export function measureLabelBox(text, fontSize = 10) {
  const display = displayLabelText(text)
  if (!display) return 0
  return Math.min(200, display.length * (fontSize * 0.58) + 10)
}

function labelCoverHalf(display, kind = 'horizontal') {
  if (kind === 'vertical') return LABEL_BOX_H / 2 + LABEL_BOX_PAD
  return measureLabelBox(display) / 2 + LABEL_BOX_PAD
}

function minLabeledVerticalSpan() {
  return LABEL_BOX_H + 2 * LABEL_BOX_PAD + 2 * ARROW_STUB_VERT
}

function anchorBottomY(pos) {
  const { h } = nodeSize(pos)
  const cy = isEventNode(pos) ? eventCy(pos) : pos.y + h / 2
  if (pos.node?.type === 'gateway') return cy + GRID.gwHalf
  return pos.y + h
}

/** Увеличить вертикальный зазор до ветки, если подписи на стрелке вниз не помещаются. */
function applyLabelBranchGaps(nodePositions, sequenceRaw, pools, poolLayouts) {
  const minSpan = minLabeledVerticalSpan()
  let branchExtra = 0

  for (const e of sequenceRaw) {
    if (!e.label?.trim()) continue
    const src = nodePositions[e.source]
    const tgt = nodePositions[e.target]
    if (!src || !tgt || src.poolId !== tgt.poolId) continue
    if ((tgt.node?.row ?? 0) <= (src.node?.row ?? 0)) continue
    if (Math.abs(nodeCx(src) - nodeCx(tgt)) >= COL_STACKED) continue

    const shaftSpan = tgt.y - anchorBottomY(src) - ARROW_HEAD
    branchExtra = Math.max(branchExtra, minSpan - shaftSpan)
  }

  if (branchExtra <= 0) return

  for (const pool of pools) {
    if (pool.role === 'participant') continue
    for (const pos of Object.values(nodePositions)) {
      if (pos.poolId !== pool.id) continue
      if ((pos.node?.row ?? 0) >= 1) pos.y += branchExtra
    }
    const pl = poolLayouts[pool.id]
    if (pl) pl.height += branchExtra
  }
}

/** Расширить зазоры между колонками под подписи на горизонтальных sequence flow. */
function applyLabelColumnGaps(nodePositions, sequenceRaw, pools, poolLayouts) {
  const baseGap = GRID.colW - GRID.nodeW

  for (const pool of pools) {
    if (pool.role === 'participant') continue
    const pl = poolLayouts[pool.id]
    const positions = Object.values(nodePositions).filter((p) => p.poolId === pool.id)
    if (!positions.length) continue

    const colExtra = {}
    for (const e of sequenceRaw) {
      if (!e.label?.trim()) continue
      const src = nodePositions[e.source]
      const tgt = nodePositions[e.target]
      if (!src || !tgt || src.poolId !== pool.id) continue
      if (Math.abs(src.y - tgt.y) > ROW_THRESH) continue

      const srcCol = src.node?.col ?? 0
      const tgtCol = tgt.node?.col ?? 0
      if (tgtCol <= srcCol) continue

      const need = measureLabelBox(e.label) + LABEL_GAP_PAD + 2 * ARROW_STUB
      const extra = Math.max(0, need - baseGap)
      if (extra <= 0) continue

      const span = tgtCol - srcCol
      for (let c = srcCol; c < tgtCol; c += 1) {
        colExtra[c] = Math.max(colExtra[c] || 0, extra / span)
      }
    }

    const cols = [...new Set(positions.map((p) => p.node?.col ?? 0))].sort((a, b) => a - b)
    const colX = {}
    let x = pl.contentX
    cols.forEach((col, idx) => {
      colX[col] = x
      if (idx < cols.length - 1) {
        x += GRID.nodeW + baseGap + (colExtra[col] || 0)
      }
    })

    for (const pos of positions) {
      const col = pos.node?.col ?? 0
      if (colX[col] != null) pos.x = colX[col]
    }
  }
}

/** Стороны якорей и форма пути по относительному положению узлов. */
function sequenceRoutePlan(src, tgt, nodePositions, priorEdges, edge, sequenceRaw) {
  const srcCx = nodeCx(src)
  const tgtCx = nodeCx(tgt)
  const dy = tgt.y - src.y

  if (Math.abs(dy) <= ROW_THRESH) {
    const goRight = tgtCx >= srcCx
    const peers = sameRowRoutePeers(edge, sequenceRaw, nodePositions)
    const peerLane = peerRouteLane(edge, sequenceRaw, nodePositions)

    if (shouldStapleRoute(src, tgt, nodePositions, edge, sequenceRaw)) {
      const staple = peerLane != null && peers.length >= 2
        ? resolveGroupedStaple(edge, peers, src, tgt, nodePositions, priorEdges, sequenceRaw)
        : pickDetourStaple(src, tgt, nodePositions, priorEdges)
      if (staple?.straight) {
        return {
          srcSide: goRight ? 'right' : 'left',
          tgtSide: goRight ? 'left' : 'right',
          srcAlign: null,
          tgtAlign: null,
          shape: Math.abs(src.y - tgt.y) < 8 ? 'straight' : 'elbow-x',
        }
      }
      return {
        srcSide: staple.side,
        tgtSide: staple.side,
        srcAlign: null,
        tgtAlign: null,
        shape: 'detour-staple',
        busY: staple.busY,
      }
    }
    return {
      srcSide: goRight ? 'right' : 'left',
      tgtSide: goRight ? 'left' : 'right',
      srcAlign: null,
      tgtAlign: null,
      shape: Math.abs(src.y - tgt.y) < 8 ? 'straight' : 'elbow-x',
    }
  }

  if (dy > ROW_THRESH) {
    const stacked = Math.abs(tgtCx - srcCx) < COL_STACKED
    return {
      srcSide: 'bottom',
      tgtSide: 'top',
      srcAlign: tgtCx,
      tgtAlign: srcCx,
      shape: 'bus-y',
    }
  }

  // Цель выше источника — возврат с ветки на основной ряд.
  const stacked = Math.abs(tgtCx - srcCx) < COL_STACKED
  if (stacked) {
    return {
      srcSide: 'top',
      tgtSide: 'bottom',
      srcAlign: tgtCx,
      tgtAlign: srcCx,
      shape: 'bus-y',
    }
  }
  if (tgtCx < srcCx) {
    return {
      srcSide: 'left',
      tgtSide: 'bottom',
      srcAlign: null,
      tgtAlign: null,
      shape: 'bus-x-up',
    }
  }
  return {
    srcSide: 'right',
    tgtSide: 'bottom',
    srcAlign: null,
    tgtAlign: null,
    shape: 'bus-x-up',
  }
}

function buildSequencePath(s, t, plan) {
  const shape = plan.shape
  if (shape === 'straight') {
    return [[s.x, s.y], [t.x, t.y]]
  }
  if (shape === 'detour-staple') {
    return [[s.x, s.y], [s.x, plan.busY], [t.x, plan.busY], [t.x, t.y]]
  }
  if (shape === 'bus-x-up') {
    return [[s.x, s.y], [t.x, s.y], [t.x, t.y]]
  }
  if (shape === 'bus-y') {
    const midY = (s.y + t.y) / 2
    return [[s.x, s.y], [s.x, midY], [t.x, midY], [t.x, t.y]]
  }
  const midX = (s.x + t.x) / 2
  return [[s.x, s.y], [midX, s.y], [midX, t.y], [t.x, t.y]]
}

function routeSequenceEdge(e, src, tgt, nodePositions, priorEdges, sequenceRaw) {
  const plan = sequenceRoutePlan(src, tgt, nodePositions, priorEdges, e, sequenceRaw)
  const s = anchor(src, plan.srcSide, plan.srcAlign)
  const t = anchor(tgt, plan.tgtSide, plan.tgtAlign)
  const points = orthPath(buildSequencePath(s, t, plan))
  const label = e.label?.trim()
    ? labelOnSegment(points, e.label)
    : { labelX: 0, labelY: 0, labelAnchor: 'middle' }

  const meta = {}
  if (e.question) {
    meta.question = e.question
    if (plan.shape === 'straight' || plan.shape === 'bus-y' || plan.shape === 'bus-x-up' || plan.shape === 'detour-staple') {
      meta.questionX = (s.x + t.x) / 2
      meta.questionY = (plan.busY ?? s.y) - 14
    } else {
      meta.questionX = (s.x + t.x) / 2
      meta.questionY = Math.min(s.y, t.y) - 22
    }
  }

  return {
    points,
    ...(plan.busY != null ? { busY: plan.busY } : {}),
    ...label,
    ...meta,
  }
}

function primaryPoolNode(edge, src, tgt, pools) {
  const primaryId = pools.find((p) => p.role === 'primary')?.id
  if (!primaryId) return src.y <= tgt.y ? src : tgt

  if (src.poolId === primaryId && tgt.poolId !== primaryId) return src
  if (tgt.poolId === primaryId && src.poolId !== primaryId) return tgt
  return src.y <= tgt.y ? src : tgt
}

function simplifyPath(points) {
  const out = []
  for (const pt of points) {
    const prev = out[out.length - 1]
    if (prev && Math.abs(prev[0] - pt[0]) < 0.5 && Math.abs(prev[1] - pt[1]) < 0.5) continue
    out.push(pt)
  }
  return out
}

const PARTICIPANT_ANCHOR_INSET = 20

function participantAnchorX(participantPos, primaryX) {
  const { w } = nodeSize(participantPos)
  return clamp(primaryX, participantPos.x + PARTICIPANT_ANCHOR_INSET, participantPos.x + w - PARTICIPANT_ANCHOR_INSET)
}

/** Разнести якоря message flow по верхней грани participant-узла. */
function buildParticipantMessageAnchors(messageRaw, nodePositions, pools) {
  const primaryId = pools.find((p) => p.role === 'primary')?.id
  const participantPoolId = pools.find((p) => p.role === 'participant')?.id
  const anchors = {}

  for (const e of messageRaw) {
    const src = nodePositions[e.source]
    const tgt = nodePositions[e.target]
    if (!src || !tgt) continue
    anchors[e.id] = { srcX: nodeCx(src), tgtX: nodeCx(tgt) }
  }

  if (!primaryId || !participantPoolId) return anchors

  const byParticipant = {}
  for (const e of messageRaw) {
    const src = nodePositions[e.source]
    const tgt = nodePositions[e.target]
    if (!src || !tgt) continue

    const participantPos = src.node?.participant && src.poolId === participantPoolId
      ? src
      : (tgt.node?.participant && tgt.poolId === participantPoolId ? tgt : null)
    if (!participantPos) continue

    const pid = participantPos.node.id
    if (!byParticipant[pid]) byParticipant[pid] = []
    byParticipant[pid].push({ e, src, tgt, participantPos })
  }

  for (const items of Object.values(byParticipant)) {
    const ordered = [...items].sort((a, b) => {
      const endA = a.src.node?.participant ? a.tgt : a.src
      const endB = b.src.node?.participant ? b.tgt : b.src
      return nodeCx(endA) - nodeCx(endB)
    })

    ordered.forEach(({ e, src, tgt, participantPos }) => {
      const primaryPos = src.node?.participant ? tgt : src
      const primaryX = nodeCx(primaryPos)
      const pX = participantAnchorX(participantPos, primaryX)
      anchors[e.id] = {
        srcX: src.node?.participant ? pX : primaryX,
        tgtX: tgt.node?.participant ? pX : primaryX,
      }
    })
  }

  return anchors
}

/** Подпись message flow: на прямой — по вертикали, на изгибе — по центру горизонтали. */
function labelOnMessagePath(pathPts, labelText) {
  const display = displayLabelText(labelText)
  if (!display) return { labelX: 0, labelY: 0, labelAnchor: 'middle' }

  if (pathPts.length === 2) {
    return labelOnSegment(orthPath(pathPts), labelText)
  }

  let bestHoriz = null
  for (let i = 0; i < pathPts.length - 1; i += 1) {
    const [x1, y1] = pathPts[i]
    const [x2, y2] = pathPts[i + 1]
    if (Math.abs(y2 - y1) >= 0.5) continue
    const len = Math.abs(x2 - x1)
    if (len < 12) continue
    if (!bestHoriz || len > bestHoriz.len) {
      bestHoriz = { x1, y1, x2, y2, len }
    }
  }

  if (bestHoriz) {
    const left = Math.min(bestHoriz.x1, bestHoriz.x2)
    const right = Math.max(bestHoriz.x1, bestHoriz.x2)
    let labelX = (left + right) / 2
    const half = labelCoverHalf(display, 'horizontal')
    const minX = left + half
    const maxX = right - half
    if (minX <= maxX) labelX = clamp(labelX, minX, maxX)
    return { labelX, labelY: bestHoriz.y1, labelAnchor: 'middle' }
  }

  return labelOnSegment(orthPath(pathPts), labelText)
}

function routeMessageEdge(src, tgt, srcX, tgtX, routeY, labelText = '') {
  const downward = src.y < tgt.y
  const fromSide = downward ? 'bottom' : 'top'
  const toSide = downward ? 'top' : 'bottom'

  const s = anchor(src, fromSide, srcX)
  const t = anchor(tgt, toSide, tgtX)

  const raw = Math.abs(s.x - t.x) < 8
    ? [[s.x, s.y], [t.x, t.y]]
    : [[s.x, s.y], [s.x, routeY], [t.x, routeY], [t.x, t.y]]
  const pathPts = simplifyPath(raw)
  const points = orthPath(pathPts)

  const label = labelText.trim()
    ? labelOnMessagePath(pathPts, labelText)
    : { labelX: 0, labelY: 0, labelAnchor: 'middle' }

  return {
    points,
    startX: s.x,
    startY: s.y,
    endX: t.x,
    endY: t.y,
    ...label,
  }
}

function getPoolGapY(poolLayouts, pools) {
  const ordered = [...pools].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
  if (ordered.length < 2) return null
  const topPl = poolLayouts[ordered[0].id]
  const bottomPl = poolLayouts[ordered[1].id]
  if (!topPl || !bottomPl) return null
  return {
    topEdge: topPl.y + topPl.height,
    bottomEdge: bottomPl.y,
    mid: (topPl.y + topPl.height + bottomPl.y) / 2,
  }
}

function snugEventNodes(nodePositions, edges) {
  const start = nodePositions.start
  const end = nodePositions.end
  if (!start && !end) return

  const startEdge = edges.find((e) => e.source === 'start')
  const endEdge = edges.find((e) => e.target === 'end')
  const gap = GRID.eventGap
  const eventW = GRID.eventR * 2

  if (start && startEdge) {
    const first = nodePositions[startEdge.target]
    if (first) {
      start.x = first.x - gap - eventW
      start.y = first.y
    }
  }

  if (end && endEdge) {
    const last = nodePositions[endEdge.source]
    if (last) {
      const { w: lastW } = nodeSize(last)
      end.x = last.x + lastW + gap
      end.y = last.y
    }
  }
}

function padPoolContent(poolLayouts, nodePositions, poolId) {
  const pl = poolLayouts[poolId]
  if (!pl) return

  const nodes = Object.values(nodePositions).filter((p) => p.poolId === poolId)
  if (!nodes.length) return

  const minX = Math.min(...nodes.map((p) => p.x))
  if (minX < pl.contentX) {
    const shift = pl.contentX - minX + 8
    nodes.forEach((p) => {
      p.x += shift
    })
    pl.width += shift
  }
}

function layoutParticipantPoolNodes(nodePositions, poolLayouts, pools, messageRaw = []) {
  const primaryId = pools.find((p) => p.role === 'primary')?.id
  const participantPool = pools.find((p) => p.role === 'participant')
  if (!primaryId || !participantPool) return

  const pl = poolLayouts[participantPool.id]
  const anchorCxsByNodeId = {}

  for (const e of messageRaw) {
    const src = nodePositions[e.source]
    const tgt = nodePositions[e.target]
    if (!src || !tgt) continue

    if (src.poolId === primaryId && tgt.poolId === participantPool.id && tgt.node?.participant) {
      const id = tgt.node.id
      if (!anchorCxsByNodeId[id]) anchorCxsByNodeId[id] = []
      anchorCxsByNodeId[id].push(nodeCx(src))
    }
    if (tgt.poolId === primaryId && src.poolId === participantPool.id && src.node?.participant) {
      const id = src.node.id
      if (!anchorCxsByNodeId[id]) anchorCxsByNodeId[id] = []
      anchorCxsByNodeId[id].push(nodeCx(tgt))
    }
  }

  const reviewPos = Object.values(nodePositions).find(
    (p) => p.poolId === primaryId && p.node?.stateCode === 'review',
  )
  const fallbackCx = reviewPos ? nodeCx(reviewPos) : null

  const participantNodes = Object.values(nodePositions).filter(
    (p) => p.poolId === participantPool.id && p.node?.participant,
  )

  for (const pos of participantNodes) {
    const linked = anchorCxsByNodeId[pos.node.id]
    if (linked?.length >= 2) {
      const minX = Math.min(...linked)
      const maxX = Math.max(...linked)
      pos.w = Math.max(GRID.nodeW, maxX - minX + 2 * PARTICIPANT_ANCHOR_INSET)
      pos.x = minX - PARTICIPANT_ANCHOR_INSET
    } else {
      const cx = linked?.[0] ?? fallbackCx
      pos.w = GRID.nodeW
      if (cx != null) pos.x = cx - pos.w / 2
    }
  }

  const byAnchor = {}
  for (const pos of participantNodes) {
    const key = Math.round(nodeCx(pos))
    if (!byAnchor[key]) byAnchor[key] = []
    byAnchor[key].push(pos)
  }
  for (const group of Object.values(byAnchor)) {
    if (group.length <= 1) continue
    group.sort((a, b) => (a.node?.col ?? 0) - (b.node?.col ?? 0))
    const cx = nodeCx(group[0])
    const step = GRID.nodeW + 24
    const totalW = (group.length - 1) * step
    group.forEach((pos, i) => {
      const { w } = nodeSize(pos)
      pos.x = cx - totalW / 2 + i * step - w / 2
    })
  }

  if (pl && participantNodes.length) {
    const minX = Math.min(...participantNodes.map((p) => p.x))
    const maxX = Math.max(...participantNodes.map((p) => p.x + nodeSize(p).w))
    const pad = 20
    pl.contentX = minX
    pl.x = minX - GRID.poolHeader - GRID.poolPad
    pl.width = maxX - pl.x + GRID.poolPad + pad
  }
}

function expandPoolBounds(poolLayouts, nodePositions) {
  let canvasW = 720
  Object.values(poolLayouts).forEach((pl) => {
    if (pl.pool.role === 'participant') {
      canvasW = Math.max(canvasW, pl.x + pl.width + GRID.margin)
      return
    }
    const nodes = Object.values(nodePositions).filter((p) => p.poolId === pl.pool.id)
    let maxRight = pl.contentX
    nodes.forEach((pos) => {
      const { w } = nodeSize(pos)
      maxRight = Math.max(maxRight, pos.x + w)
    })
    const needed = maxRight - pl.x + GRID.poolPad + GRID.poolHeader
    if (needed > pl.width) pl.width = needed
    canvasW = Math.max(canvasW, pl.x + pl.width + GRID.margin)
  })
  return canvasW
}

export function buildLayout(spec) {
  if (!spec?.nodes?.length) return null

  const pools = [...(spec.pools || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
  if (!pools.length && spec.doc_type) {
    pools.push({ id: spec.doc_type, name: spec.title, role: 'primary', order: 0 })
  }

  const poolLayouts = {}
  let y = GRID.margin
  let canvasW = 720

  for (const pool of pools) {
    const poolNodes = spec.nodes.filter(
      (n) => n.poolId === pool.id || (!n.poolId && pool.role === 'primary'),
    )
    const layoutNodes = poolNodes.filter((n) => n.type !== 'start' && n.type !== 'end')
    const cols = layoutNodes.map((n) => n.col ?? 0)
    const rows = poolNodes.map((n) => n.row ?? 0)
    const minCol = Math.min(...cols, 0)
    const maxCol = Math.max(...cols, 0)
    const maxRow = Math.max(...rows, 0)
    const branchExtra = maxRow > 0 ? GRID.branchRowH - GRID.rowH + 20 : 0
    const isParticipant = pool.role === 'participant'
    const width = isParticipant
      ? GRID.poolPad * 2 + GRID.poolHeader + GRID.nodeW + 48
      : GRID.poolPad * 2 + GRID.poolHeader + (maxCol - minCol + 2) * GRID.colW + 80
    const height = isParticipant
      ? GRID.poolPad * 2 + GRID.poolHeader + GRID.nodeH + 20
      : GRID.poolPad * 2 + GRID.poolHeader + GRID.rowH + branchExtra + 24
    canvasW = Math.max(canvasW, width + GRID.margin * 2)

    poolLayouts[pool.id] = {
      pool,
      x: GRID.margin,
      y,
      width,
      height,
      minCol,
      contentX: GRID.margin + GRID.poolPad + GRID.poolHeader,
      contentY: y + GRID.poolPad + GRID.poolHeader,
    }
    y += height + GRID.poolGap
  }

  const nodePositions = {}
  for (const pool of pools) {
    const pl = poolLayouts[pool.id]
    const poolNodes = spec.nodes.filter(
      (n) => n.poolId === pool.id || (!n.poolId && pool.role === 'primary'),
    )
    for (const n of poolNodes) {
      const col = n.col ?? 0
      const row = n.row ?? 0
      const yOff = row === 0 ? 0 : GRID.rowH + (row - 1) * GRID.branchRowH
      nodePositions[n.id] = {
        x: pl.contentX + (col - pl.minCol) * GRID.colW,
        y: pl.contentY + yOff,
        poolId: pool.id,
        node: n,
      }
    }
  }

  const messageRaw = (spec.edges || []).filter((e) => e.crossDoc || e.edgeKind === 'message')
  const sequenceRaw = (spec.edges || []).filter((e) => !e.crossDoc && e.edgeKind !== 'message')

  applyLabelColumnGaps(nodePositions, sequenceRaw, pools, poolLayouts)
  applyLabelBranchGaps(nodePositions, sequenceRaw, pools, poolLayouts)
  snugEventNodes(nodePositions, spec.edges || [])
  const primaryPool = pools.find((p) => p.role === 'primary')
  if (primaryPool) padPoolContent(poolLayouts, nodePositions, primaryPool.id)

  layoutParticipantPoolNodes(nodePositions, poolLayouts, pools, messageRaw)

  const sequenceEdges = []
  const sequenceOrder = [...sequenceRaw].sort((a, b) => {
    const prioA = edgeRoutePriority(a, nodePositions, sequenceRaw)
    const prioB = edgeRoutePriority(b, nodePositions, sequenceRaw)
    if (prioA !== prioB) return prioA - prioB
    return String(a.id).localeCompare(String(b.id))
  })
  for (const e of sequenceOrder) {
    const src = nodePositions[e.source]
    const tgt = nodePositions[e.target]
    if (!src || !tgt) continue
    const routed = routeSequenceEdge(e, src, tgt, nodePositions, sequenceEdges, sequenceRaw)
    sequenceEdges.push({ ...e, ...routed })
  }

  canvasW = expandPoolBounds(poolLayouts, nodePositions)

  const gap = getPoolGapY(poolLayouts, pools)
  const msgAnchors = buildParticipantMessageAnchors(messageRaw, nodePositions, pools)
  const msgMetas = messageRaw
    .map((e) => {
      const src = nodePositions[e.source]
      const tgt = nodePositions[e.target]
      if (!src || !tgt) return null
      const pts = msgAnchors[e.id] || { srcX: nodeCx(src), tgtX: nodeCx(tgt) }
      return {
        e,
        src,
        tgt,
        srcX: pts.srcX,
        tgtX: pts.tgtX,
        sortX: (pts.srcX + pts.tgtX) / 2,
        downward: src.y < tgt.y,
      }
    })
    .filter(Boolean)
    .sort((a, b) => a.sortX - b.sortX)

  const downMetas = msgMetas.filter((m) => m.downward)
  const upMetas = msgMetas.filter((m) => !m.downward)

  const assignRouteY = (list, bias) => {
    if (!list.length) return []
    const xs = list.map((m) => m.sortX)
    const minX = Math.min(...xs)
    const maxX = Math.max(...xs)
    const span = maxX - minX || 1

    return list.map((meta) => {
      const spread = ((meta.sortX - minX) / span - 0.5) * GRID.msgLaneStep * 2
      const routeY = gap ? gap.mid + bias + spread : (meta.src.y + meta.tgt.y) / 2
      const clampedY = gap
        ? clamp(routeY, gap.topEdge + 20, gap.bottomEdge - 20)
        : routeY
      return { ...meta, routeY: clampedY }
    })
  }

  const messageEdges = [
    ...assignRouteY(downMetas, 18),
    ...assignRouteY(upMetas, -18),
  ].map((meta, laneIndex) => ({
    ...meta.e,
    ...routeMessageEdge(meta.src, meta.tgt, meta.srcX, meta.tgtX, meta.routeY, meta.e.label || ''),
    channel: laneIndex,
  }))

  return {
    pools,
    poolLayouts,
    nodePositions,
    sequenceEdges,
    messageEdges,
    width: canvasW,
    height: y + GRID.margin,
  }
}
